"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"

function isUpdater<T>(x: T | ((v: T) => T) | undefined): x is (v: T) => T {
  return typeof x === "function"
}

export function useQueryState<T = string | string[]>(
  name: string,
  { keep, multi }: { keep?: string[]; multi?: boolean } = {},
): [T | undefined, (value: T | ((v: T) => T) | undefined) => void] {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()

  const all = searchParams.getAll(name)
  const queryValue = all.length === 0 ? undefined : all.length === 1 ? all[0] : all
  const value = queryValue as T | undefined

  const setQueryState = (v: T | ((v: T) => T) | undefined) => {
    const val = isUpdater<T>(v) ? v(value as T) : v

    const params = new URLSearchParams()
    if (keep) {
      for (const key of keep) {
        for (const item of searchParams.getAll(key)) params.append(key, item)
      }
    } else {
      for (const [key, item] of searchParams.entries()) params.append(key, item)
    }

    params.delete(name)
    if (val !== undefined) {
      if (Array.isArray(val)) for (const item of val) params.append(name, `${item}`)
      else params.set(name, `${val}`)
    }

    const query = params.toString()
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false })
  }

  return [(multi && value && !Array.isArray(value) ? [value] : value) as T | undefined, setQueryState]
}
